<template>
  <div class="agreement">
    <div class="main-title">
      {{ appName }} User Agreement
    </div>

    <div class="T_title">Preface</div>
    <div class="T_content">
      Welcome to {{ appName }} (hereinafter referred to as the "Platform" or
      "APP"). This Platform is provided and operated by {{ appName }}
      (collectively referred to as "{{ appName }}", "we" or "us").
      <br /><br />
      The Terms of Service you are reading (hereinafter referred to as "these
      Terms") constitute a legally binding contract between you and us. They set
      forth all rights and obligations governing your access to and use of this
      APP, affiliated websites, products, functions, content and all supporting
      services (collectively the "Services").
      <br /><br />
      Certain functions (including content publishing, recharge payment, revenue
      withdrawal, etc.) are subject to age restrictions and may not be available
      to all users. The Services may only be used by you for private,
      non-commercial purposes. In these Terms, "you" and "your" refer to you as
      the user of the Services.
    </div>

    <div class="subtitle">Age Notice</div>
    <div class="T_content">
      Please read these Terms in full. If you are under 18 years old, you may
      only use the Services with full knowledge and written consent of your
      parents or legal guardians, who shall accompany you to read and understand
      all contents herein. Users must be at least 18 years old to access paid
      functions and revenue withdrawal features.
      <br /><br />
      By clicking "Agree", registering an account, logging in, using any
      function, making payments or consuming virtual assets, you acknowledge
      that you have fully read, understood and unconditionally accepted all
      clauses of these Terms. If you disagree with any content hereof, you shall
      immediately stop downloading, registering and using all {{ appName }}
      Services.
    </div>

    <div class="T_title">1. Revision of Terms</div>
    <div class="T_content">
      1. We may revise these Terms from time to time based on product iteration,
      cross-business integration, regulatory policy adjustments and business
      optimization.
      <br /><br />
      2. For material changes to these Terms, we will notify all users through
      commercially reasonable means including in-app announcements and pop-up
      notifications. You are also recommended to review these Terms regularly
      to stay updated on the latest rules.
      <br /><br />
      3. Your continued login or use of the Services after the revised Terms take
      effect shall be deemed as your voluntary acceptance of the updated
      version. If you do not agree with the revised content, you must cease all
      use of {{ appName }} Services immediately.
    </div>

    <div class="T_title">2. User Account Management Rules</div>
    <div class="subtitle">2.1 Account Registration &amp; Authorization</div>
    <div class="T_content">
      Access to most Services requires account registration or a temporary
      account assigned by the Platform. We reserve all rights to adjust
      registration methods and verification rules. All information you submit
      during registration must be true, complete and valid.
    </div>
    <div class="subtitle">2.2 Cross-Business Account Authorization</div>
    <div class="T_content">
      By registering a {{ appName }} account, you automatically authorize us to
      activate such account for login and use of other affiliated products under
      our company, subject to the actual available products launched by the
      Platform.
    </div>
    <div class="subtitle">2.3 Account Ownership &amp; Transfer Prohibition</div>
    <div class="T_content">
      All accounts are owned by {{ appName }}, and you are granted only limited
      right of use. You are strictly prohibited from gifting, lending, renting,
      transferring, selling or sharing your account with any third party. Any
      private account transaction shall be null and void, and you shall bear all
      losses arising therefrom.
    </div>
    <div class="subtitle">2.4 Account Security &amp; Liability Allocation</div>
    <div class="T_content">
      1. You shall be fully responsible for safeguarding your account, password,
      login verification codes, bound device information and other security
      credentials. If your account is stolen or impersonated due to improper
      operation, password leakage or device sharing on your part, you shall bear
      all legal liabilities and property losses incurred.
      <br /><br />
      2. All operations, published content, payments, consumption and
      interactive behaviors under your account shall be deemed acts performed by
      yourself, and you shall take full responsibility for all activities under
      your account.
      <br /><br />
      3. If you discover suspected account theft, password leakage or
      unauthorized login, please contact customer service immediately to freeze
      your account. You are forbidden to log in or misappropriate other users'
      accounts under any circumstances.
    </div>
    <div class="subtitle">2.5 Account Restriction, Suspension &amp; Termination</div>
    <div class="T_content">
      If you violate these Terms, applicable laws and regulations or the
      Platform Community Guidelines, we reserve the right to partially restrict
      functions, temporarily freeze your account or permanently terminate your
      access to the Services without prior notice.
    </div>
    <div class="subtitle">2.6 Disposal of Long-Term Inactive Accounts</div>
    <div class="T_content">
      If your account has no valid operations including login, interaction or
      recharge for 12 consecutive months, we reserve the right to cancel and
      recover the account without separate notice, and clear all virtual assets,
      published content and revenue balances stored therein. You shall bear all
      relevant risks and losses arising therefrom. Meanwhile, we retain the
      discretionary right to recover any account or username at any time.
    </div>

    <div class="T_title">3. Privacy Protection</div>
    <div class="T_content">
      Your personal privacy is extremely important to us. Complete rules
      governing the collection, storage, use, disclosure and permission management
      of your personal information are set forth in the
      <em>{{ appName }} Privacy Policy</em>. The Privacy Policy is an
      inseparable supplementary document to these Terms and shall have the same
      legal effect.
    </div>

    <div class="T_title">4. Service Usage Specifications &amp; Prohibited Acts</div>
    <div class="subtitle">4.1 Definition of User Content</div>
    <div class="T_content">
      User Content refers to all materials you create, upload, copy, send,
      publish or share via the Platform, including but not limited to avatars,
      nicknames, personal profiles, texts, images, audios, videos, private
      messages, comments, links, posts and live streaming materials.
    </div>
    <div class="subtitle">4.2 Prohibited Content &amp; Conduct</div>
    <div class="T_content">
      You shall not use the Services to create, upload, forward or disseminate the
      following content, nor engage in the prohibited acts below:
      <br /><br />
      1. Endangering national security, disclosing state secrets, subverting state
      power or undermining national unity;
      <br /><br />
      2. Harming national honor and national interests;
      <br /><br />
      3. Inciting ethnic hatred, ethnic discrimination or disrupting ethnic
      solidarity;
      <br /><br />
      4. Violating national religious policies, advocating cults or feudal
      superstition;
      <br /><br />
      5. Fabricating and spreading rumors, disturbing public order or
      destabilizing society;
      <br /><br />
      6. Disseminating pornographic, vulgar, sexually suggestive or obscene
      content;
      <br /><br />
      7. Spreading materials related to gambling, violence, homicide or terrorism;
      <br /><br />
      8. Insulting, slandering or falsely accusing others, infringing upon
      others' legitimate rights or committing fraud;
      <br /><br />
      9. Posting abusive, intimidating or threatening content targeting others;
      <br /><br />
      10. Sending harassing messages, spam advertisements, malicious links or
      phishing content;
      <br /><br />
      11. Disclosing, stealing or exposing third parties' privacy, identity
      documents or location data;
      <br /><br />
      12. Infringing upon others' right of reputation, portrait rights,
      copyrights, trademarks, trade secrets and other intellectual property
      rights;
      <br /><br />
      13. Any content that endangers minors' physical and mental health or induces
      minors to engage in improper behaviors;
      <br /><br />
      14. Tracking, harassing or doxxing other Platform users;
      <br /><br />
      15. Forging information, stealing others' avatars or personal data, and
      impersonating others to publish content;
      <br /><br />
      16. Coercing or inducing other users to like, follow, click external links
      or forward information;
      <br /><br />
      17. Fabricating facts or concealing the truth to mislead or defraud other
      users;
      <br /><br />
      18. Purely commercial promotion, product sales or diversion content;
      <br /><br />
      19. Registering and operating multiple fake accounts via scripts or batch
      tools;
      <br /><br />
      20. Deleting or tampering with Platform copyright marks and watermarks;
      <br /><br />
      21. Conducting reverse engineering, decompilation, cracking or attempting to
      extract the source code of the APP;
      <br /><br />
      22. Without our written permission, copying, renting, modifying, building
      mirror websites or redistributing materials involving the Platform's
      intellectual property;
      <br /><br />
      23. Tampering with or hijacking Platform interface data, developing
      derivative tools or plugins;
      <br /><br />
      24. Modifying or forging service instructions and data, altering APP
      functions, and distributing modified versions of the APP to the public
      whether for commercial purposes or not;
      <br /><br />
      25. Logging in or using the Services through unauthorized third-party
      plugins, cheats, scripts or cracking tools, or creating, publishing and
      distributing such unauthorized tools;
      <br /><br />
      26. Posting offensive, vulgar or widely objectionable inappropriate
      content;
      <br /><br />
      27. Other content or acts that violate laws and regulations of your
      residence and the Platform's place of operation, infringe third parties'
      legitimate rights and interests, or interfere with the normal operation of
      the Platform.
    </div>
    <div class="subtitle">4.3 Content Review &amp; Violation Disposal</div>
    <div class="T_content">
      All User Content uploaded by you will be reviewed by the Platform within 24
      hours. If any content violates the above prohibitions, the Platform
      reserves the right to delete such content directly. For serious violations,
      repeated infringements or suspected criminal acts, your account will be
      permanently banned, violation records will be retained, and we will
      cooperate with regulatory and judicial authorities for investigation.
    </div>
    <div class="subtitle">4.4 Liability Attribution for User Content</div>
    <div class="T_content">
      All User Content published and disseminated by you solely represents your
      personal opinions and bears no connection with {{ appName }}, nor does it
      reflect the Platform's stance. The Platform shall not assume any legal
      liability for User Content.
    </div>
    <div class="subtitle">4.5 User Warranties &amp; Indemnification Obligations</div>
    <div class="T_content">
      You warrant that all uploaded and published content is true, legal and free
      of infringement risks, and you shall independently bear all consequences
      arising from your acts. If your conduct causes reputational, property or
      legal losses to the Platform or any third party (including attorney fees,
      notarization fees and rights protection costs), you shall make full
      compensation, and we reserve the right to pursue liability against you in
      accordance with these Terms.
    </div>

    <div class="T_title">5. Intellectual Property Clauses</div>
    <div class="subtitle">5.1 Platform-Owned Intellectual Property</div>
    <div class="T_content">
      All Platform materials within the APP (including interface design, texts,
      icons, images, videos, software programs, audios, page layout, special
      effect materials, excluding User Content) and all intellectual property
      rights therein belong to {{ appName }} or its authorized third-party
      licensors.
      <br /><br />
      Downloading the APP or purchasing virtual props does not grant you any
      ownership of Platform materials. Without our prior written consent, you are
      prohibited from modifying, copying, scraping, distributing, reprinting,
      selling or commercially reusing any Platform materials in whole or in
      part. All rights not explicitly granted to you herein are fully reserved by
      the Platform.
    </div>
    <div class="subtitle">5.2 Intellectual Property Rights of User-Generated Content (UGC)</div>
    <div class="T_content">
      1. You warrant that all UGC uploaded by you is original, and you hold
      complete copyright, portrait rights and other relevant rights to such
      content, with full authority to grant all licenses specified in these
      Terms. Any third-party claims arising from content infringement shall be
      borne solely by you, and the Platform shall not be held liable.
      <br /><br />
      2. By uploading or publishing any UGC, you automatically grant
      {{ appName }} a perpetual, worldwide, non-exclusive, transferable,
      sublicensable, royalty-free license to use, host, reproduce, edit, adapt,
      translate, create derivative works of, distribute, publicly perform and
      display your UGC for Platform operation, product promotion and campaign
      publicity without additional remuneration.
      <br /><br />
      3. The Platform does not warrant the accuracy, completeness or quality of
      UGC. The Platform shall not be liable for any inappropriate UGC you may
      encounter during use. We may delete any User Content at our sole discretion
      without prior notice. You may contact customer service to report infringing
      content.
    </div>

    <div class="T_title">6. Paid Services, Virtual Assets &amp; Revenue Withdrawal Rules</div>
    <div class="subtitle">6.1 Basic Services &amp; Paid Functions</div>
    <div class="T_content">
      Basic functions of the APP including browsing, chatting and posting feeds
      are permanently free of charge. Certain special effects, paid live rooms,
      exclusive props and advanced interactive functions require payment to
      unlock, with specific prices and billing rules displayed on in-app pages.
    </div>
    <div class="subtitle">6.2 Virtual Currency ({{ appName }} Diamonds) Rules</div>
    <div class="T_content">
      1. "{{ appName }} Diamonds" obtained via recharge or activities are merely
      in-platform virtual props with no physical property attributes. Ownership
      of all virtual diamonds belongs to the Platform, and you only enjoy limited
      usage rights.
      <br /><br />
      2. Virtual diamonds are non-refundable, non-transferable, non-tradable and
      cannot be exchanged for legal tender, cash or external assets. Diamonds have
      an expiration date and will be cleared automatically upon expiry without
      compensation.
      <br /><br />
      3. The Platform reserves the right to adjust diamond prices, scope of use
      and distribution rules at any time, or discontinue the diamond system
      without prior notice, refund or compensation to users.
      <br /><br />
      4. If your account is cancelled, banned or recovered due to long-term
      inactivity, all unused diamonds stored in the account will be cleared with
      no refund or compensation provided.
      <br /><br />
      5. The Platform shall not be obligated to restore, reissue or compensate for
      abnormal diamond consumption caused by system maintenance, function
      adjustments or program failures.
    </div>
    <div class="subtitle">6.3 Payment Channel Statement</div>
    <div class="T_content">
      Paid services can be purchased through third-party payment channels such as
      Apple iTunes and Google Play, with bills issued by such third parties. The
      service terms of third-party payment platforms are formulated independently
      and beyond our control; you shall read and abide by third-party payment
      rules on your own.
    </div>
    <div class="subtitle">6.4 User Revenue Withdrawal Rules</div>
    <div class="T_content">
      1. You may generate revenue through Platform activities, user gifts and
      brand cooperation. Such revenue will be accumulated in the form of virtual
      points within your account and can only be withdrawn once all applicable
      conditions are met.
      <br /><br />
      2. Mandatory withdrawal eligibility requirements:
      <br /><br />
      (1) You have reached the age of 18, or the legal age of majority in your
      country of citizenship or residence (whichever is higher);
      <br /><br />
      (2) All revenue sources are legal and not derived from illegal or grey-area
      activities;
      <br /><br />
      (3) Your account has no violation records against these Terms, Platform
      Community Guidelines or supplementary supporting rules.
      <br /><br />
      3. If you fail to meet the above conditions, withdrawal applications will be
      rejected. You may contact customer service to inquire about rectification
      measures, but the Platform shall not be liable for any losses incurred due
      to unavailable withdrawal.
      <br /><br />
      4. If your account is found in violation of these Terms, the Platform
      reserves the right to freeze and deduct all unreleased revenue without
      refund.
    </div>
    <div class="subtitle">6.5 Service Interruption Exemption</div>
    <div class="T_content">
      The Platform will conduct regular and unscheduled server maintenance and
      version upgrades. The Platform shall not compensate users for impacts on
      functions, revenue or virtual assets caused by service interruptions
      within a reasonable period, and we will issue announcements as far as
      possible in advance.
    </div>

    <div class="T_title">7. Risk Warnings</div>
    <div class="subtitle">7.1 All Usage Risks Assumed by Users</div>
    <div class="T_content">
      {{ appName }} only serves as an information sharing and social interaction
      platform, and provides no warranty for all account operations, authenticity
      or value of content. You shall independently judge the authenticity of all
      information on the Platform and bear all losses arising from reliance on
      inaccurate content, for which the Platform shall not be liable.
    </div>
    <div class="subtitle">7.2 Obligation to Report Violations</div>
    <div class="T_content">
      If you discover other users posting violating content or engaging in
      infringing or illegal acts, please notify customer service immediately, and
      we will dispose of such acts in accordance with these Terms.
    </div>
    <div class="subtitle">7.3 Platform Right to Adjust Services</div>
    <div class="T_content">
      We reserve the discretionary right to modify, suspend or discontinue all or
      part of the Service functions at any time without prior notice to users.
    </div>
    <div class="subtitle">7.4 Exemption for Offline Interaction Risks</div>
    <div class="T_content">
      If you meet other users offline or conduct private transactions and fund
      transfers through connections established on the Platform, all risks of
      personal and property safety as well as transaction disputes shall be borne
      solely by you. The Platform shall not assume any compensation or mediation
      liability.
    </div>

    <div class="T_title">8. Breach Remedies &amp; Liability Assumption</div>
    <div class="subtitle">8.1 Platform Remedial Measures for Breach</div>
    <div class="T_content">
      If you commit illegal acts, regulatory violations, breaches of contract or
      infringements upon third-party rights, the Platform may adopt any or a
      combination of the following measures at its sole discretion:
      <br /><br />
      1. Issue an in-app warning;
      <br /><br />
      2. Restrict partial functions (content posting, private messaging, recharge,
      withdrawal);
      <br /><br />
      3. Temporarily freeze your account;
      <br /><br />
      4. Permanently ban your account and add you to the blacklist;
      <br /><br />
      5. Publicize your breach records;
      <br /><br />
      6. Deduct and confiscate all virtual props and unreleased revenue in your
      account.
      <br /><br />
      If your account is permanently banned or blacklisted, all virtual assets and
      revenue shall not be refunded or compensated.
    </div>
    <div class="subtitle">8.2 Obligation to Cooperate with Regulatory &amp; Judicial Authorities</div>
    <div class="T_content">
      You acknowledge that if your acts are suspected of illegal conduct, the
      Platform reserves the right to retain all operation logs, account
      information and content data, and submit such materials to public security,
      cyberspace administration, judicial and other competent authorities in
      compliance with applicable laws. You shall bear all legal consequences
      arising therefrom.
    </div>
    <div class="subtitle">8.3 Full Compensation Liability</div>
    <div class="T_content">
      If your breach of contract or infringement causes losses to the Platform or
      any third party (including attorney fees, litigation costs and notarization
      fees), you shall bear full compensation liability.
    </div>
    <div class="subtitle">8.4 User Indemnification Commitment</div>
    <div class="T_content">
      You agree to indemnify, defend and hold {{ appName }} and its affiliated
      companies harmless from all claims, lawsuits, losses, expenses and
      liabilities (including attorney fees) arising out of your access to the
      Services, breach of these Terms or infringement of third-party rights via
      your User Content.
    </div>

    <div class="T_title">9. Disclaimer (To the Maximum Extent Permitted by Applicable Law)</div>
    <div class="subtitle">9.1 Force Majeure Exemption</div>
    <div class="T_content">
      The Platform shall not be liable for service suspension or user losses
      caused by events beyond our control and foresight, including natural
      disasters (floods, earthquakes, typhoons), wars, social unrest and government
      regulatory measures. We will make reasonable efforts to repair Services as
      soon as possible.
    </div>
    <div class="subtitle">9.2 No Warranty for Service Stability</div>
    <div class="T_content">
      The Platform does not warrant that the APP will remain permanently secure,
      stable, uninterrupted, error-free or delay-free. We provide no express or
      implied warranties regarding the Services, including merchantability,
      non-infringement and fitness for a specific purpose. We cannot control all
      user behaviors and content, and shall not be liable for vulgar, false or
      infringing content posted by other users.
    </div>
    <div class="subtitle">9.3 Exemption for Network &amp; Device Security Risks</div>
    <div class="T_content">
      All information leakage and property losses caused by network fluctuations,
      viruses and Trojans in third-party software, user device failures or
      improper password storage shall be borne solely by you.
    </div>
    <div class="subtitle">9.4 Exemption for Third-Party Content &amp; Third-Party Services</div>
    <div class="T_content">
      External links, third-party advertisements and cooperative content within
      the APP may contain misleading, fraudulent or infringing information, for
      which the Platform shall not be liable for any losses incurred by you or
      third parties. Disputes arising from third-party payment and login tools
      shall be resolved between you and the relevant third-party entity.
    </div>
    <div class="subtitle">9.5 Exemption for Indirect Losses</div>
    <div class="T_content">
      To the extent permitted by law, the Platform shall not be liable for any
      indirect, incidental, special, exemplary or consequential damages,
      including loss of profits, goodwill or data loss arising from use or
      inability to use the Services, or acts of third parties including other
      users and external website operators. Regardless of the cause of liability,
      our total aggregate compensation limit shall not exceed the total fees you
      have paid to us (zero if no payment has been made).
    </div>
    <div class="subtitle">9.6 Free Service Disclaimer</div>
    <div class="T_content">
      The Platform shall not be liable for any functional defects or losses arising
      from all free functions provided to users.
    </div>

    <div class="T_title">10. Miscellaneous Provisions</div>
    <div class="subtitle">10.1 Reminder of Core Clauses</div>
    <div class="T_content">
      Clauses concerning disclaimers, risk assumption, account ownership,
      liability limitation and non-refund of virtual assets constitute core
      important terms herein. Minors must read these Terms in full under
      guardians' accompaniment.
    </div>
    <div class="subtitle">10.2 Validity of Supplementary Rules</div>
    <div class="T_content">
      Community guidelines, recharge rules, activity detailed rules, withdrawal
      rules and all announcements published within the APP are supplementary
      documents to these Terms, with equal legal effect, and together form a
      complete binding contract.
    </div>
    <div class="subtitle">10.3 Governing Law &amp; Dispute Resolution</div>
    <div class="T_content">
      1. The formation, performance and interpretation of these Terms shall be
      governed by the laws of the Hong Kong Special Administrative Region of the
      People's Republic of China.
      <br /><br />
      2. All disputes arising out of or in connection with these Terms and the
      use of the Services (including disputes over the existence, validity and
      termination of these Terms) that cannot be resolved through negotiation
      shall be submitted to the Hong Kong International Arbitration Centre
      (HKIAC) for final arbitration in accordance with its arbitration rules
      effective at the time of submission. Arbitral awards shall be final and
      binding on both parties.
    </div>
    <div class="subtitle">10.4 Severability</div>
    <div class="T_content">
      If any clause of these Terms is ruled invalid or unenforceable by a
      competent authority, such ruling shall not affect the validity of the
      remaining clauses. Both parties shall negotiate in good faith to replace the
      invalid clause with alternative terms to achieve the original commercial
      intent as far as practicable.
    </div>
    <div class="subtitle">10.5 Contact Information</div>
    <div class="T_content">
      If you have questions, comments or complaints regarding these Terms or
      Platform Services, please send an email to our official customer service
      mailbox: {{ appEmail }}. We will review and respond promptly.
    </div>
    <div class="subtitle">10.6 Effective Date</div>
    <div class="T_content">
      These Terms shall take effect on July 7, 2026, and apply to all new and
      existing users of {{ appName }}.
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {};
  },
  created() {},
};
</script>

<style scoped>
.agreement {
  padding: 0px 25px;
  color: #666;
}
.main-title {
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1.4;
  margin: 0.5333rem 0 0.8rem;
  color: #000;
  text-align: center;
}
.T_title {
  font-size: 1.3rem;
  font-weight: 600;
  margin: 0.5333rem 0px;
  color: #000;
}
.subtitle {
  font-size: 1.1rem;
  color: #333;
  margin: 0.4rem 0 0.2rem;
}
.T_content {
  font-size: 1rem;
  margin-bottom: 0.4667rem;
  color: #666;
}
</style>
